import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { CashDeskUserOpen } from "src/app/model/cash-desk-user-open";
import { DocumentationItem } from "src/app/model/documentation-item";
import { Plan } from "src/app/model/entity";
import { IntegratedAdmissionAppointment } from "src/app/model/integrated-admission/integrated-admission-appointment";
import { IntegratedAdmissionAppointmentObservation } from "src/app/model/integrated-admission/integrated-admission-appointment-observation";
import { IntegratedAdmissionAppointmentPlan } from "src/app/model/integrated-admission/integrated-admission-appointment-plan";
import { IntegratedAdmissionAppointmentValidation } from "src/app/model/integrated-admission/integrated-admission-appointment-validation";
import { MedicalOrderScann } from "src/app/model/medical-order-scann";
import { PracticeRegionSearchReservation } from "src/app/model/practice-region-reservation";
import { ValorizationEdit } from "src/app/model/valorization-edit";
import { ValorizationItemAmount } from "src/app/model/valorization-item";
import { AuthService } from "src/app/services/auth.service";
import { BudgetService } from "src/app/services/budget/budget.service";
import { ConfirmDialogService } from "src/app/services/confirm-dialog.service";
import { IntegratedAdmissionService } from "src/app/services/integrated-admission/integrated-admission.service";
import { InvoiceCashDeskService } from "src/app/services/invoice-cash-desk/invoice-cash-desk.service";
import { NotificationToastService } from "src/app/services/notification-toast.service";
import { OnLineValidationService } from "src/app/services/on-line-validation/on-line-validation.service";
import { PracticeService } from "src/app/services/practice.service";
import { ValorizationService } from "src/app/services/valorization/valorization.service";
import { BudgetListDialogComponent } from "../../dialogs/budget-list-dialog/budget-list-dialog.component";
import { MatDialog } from "@angular/material";
import { BudgetFilter } from "src/app/model/budget/budget-filter";
import { isValidMedicalOrderDate } from "src/app/validators/medical-order-edit.validator";
import { Normative } from "src/app/model/normative";
import { NormativeService } from "src/app/services/normative/normative.service";
import { ReservationService } from "src/app/services/reservation.service";
import { AppointmentWaitDialogComponent } from "../../dialogs/appointment-wait-dialog/appointment-wait-dialog.component";
import { MedicalAppointmentWaitSearchItem } from "src/app/model/medical-appointment/medical-appointment-wait-search-item";
import { EstadoTurnoEspera } from "src/app/model/medical-appointment/medical-appointment-wait-state";
import { MedicalAppointmentValidationProcedure } from "src/app/model/medical-appointment-validation-procedure";
import { AdmissionGroupedProcedureRegionService } from "src/app/services/admission-grouped-procedure-region/admission-grouped-procedure-region.service";
import { PatientPhoneFormatControlDialogComponent } from "../integrated-admission-patient/patient-phone-format-control-dialog/patient-phone-format-control-dialog.component";
import { PatientService } from "src/app/services/patient.service";
import { LaboratoryAppointmentQuestionService } from "src/app/services/laboratory-appointment-question/laboratory-appointment-question.service";
import { LaboratoryAppointmentQuestionDialogComponent } from "../../dialogs/laboratory-appointment-question-dialog/laboratory-appointment-question-dialog.component";

@Injectable({
  providedIn: "root",
})
export abstract class IntegratedAdmissionWizardService {
  centerId: number;
  sectorId: number;
  patientId: number;
  readonly: boolean = false;
  proceduresGroupedExcecuted: boolean = false;
  proceduresGroupedRequired: boolean = false;
  appointments: IntegratedAdmissionAppointment[] = [];
  procedures: PracticeRegionSearchReservation[] = [];
  currentAppointment: IntegratedAdmissionAppointment;
  cashDeskUserOpen: CashDeskUserOpen;
  numberOfExtractedProcedures: number = 0;
  phoneValidated: boolean = false;

  _dataAppointmentsSubject: BehaviorSubject<IntegratedAdmissionAppointment[]> =
    new BehaviorSubject<IntegratedAdmissionAppointment[]>([]);

  appointments$: Observable<IntegratedAdmissionAppointment[]> =
    this._dataAppointmentsSubject.asObservable();

  _dataCurrentAppointmentSubject: BehaviorSubject<IntegratedAdmissionAppointment> =
    new BehaviorSubject<IntegratedAdmissionAppointment>(null);

  currentAdmissionAppointment$: Observable<IntegratedAdmissionAppointment> =
    this._dataCurrentAppointmentSubject.asObservable();

  _dataCurrentPlanSubject: BehaviorSubject<IntegratedAdmissionAppointmentPlan> =
    new BehaviorSubject<IntegratedAdmissionAppointmentPlan>(null);

  currentPlanSubject$: Observable<IntegratedAdmissionAppointmentPlan> =
    this._dataCurrentPlanSubject.asObservable();

  _dataCurrentProceduresSubject: BehaviorSubject<
    PracticeRegionSearchReservation[]
  > = new BehaviorSubject<PracticeRegionSearchReservation[]>([]);

  currentProceduresSubject$: Observable<PracticeRegionSearchReservation[]> =
    this._dataCurrentProceduresSubject.asObservable();

  _dataCurrentValorizationSubject: BehaviorSubject<ValorizationEdit> =
    new BehaviorSubject<ValorizationEdit>(null);

  currentValorizationSubject$: Observable<ValorizationEdit> =
    this._dataCurrentValorizationSubject.asObservable();

  _dataCurrentValidatoinSubject: BehaviorSubject<IntegratedAdmissionAppointmentValidation> =
    new BehaviorSubject<IntegratedAdmissionAppointmentValidation>(null);

  currentValidationSubject$: Observable<IntegratedAdmissionAppointmentValidation> =
    this._dataCurrentValidatoinSubject.asObservable();

  _dataLoadingValorizationSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  currentLoadingValorizationSubject$: Observable<boolean> =
    this._dataLoadingValorizationSubject.asObservable();

  _dataNormativeSubject: BehaviorSubject<Normative> =
    new BehaviorSubject<Normative>(null);

  currentNormativeSubject$: Observable<Normative> =
    this._dataNormativeSubject.asObservable();

  _dataReadOnlySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  currentReadOnlySubject$: Observable<boolean> =
    this._dataReadOnlySubject.asObservable();

  _dataValidationProceduresSubject: BehaviorSubject<
    MedicalAppointmentValidationProcedure[]
  > = new BehaviorSubject<MedicalAppointmentValidationProcedure[]>([]);

  currentValidationProceduresSubject$: Observable<
    MedicalAppointmentValidationProcedure[]
  > = this._dataValidationProceduresSubject.asObservable();

  _dataPatientCellPhoneSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>("");

  currentPatientCellPhoneSubject$: Observable<string> =
    this._dataPatientCellPhoneSubject.asObservable();

  constructor(
    public authService: AuthService,
    public patientService: PatientService,
    public router: Router,
    public notificationToastService: NotificationToastService,
    public onLineValidationService: OnLineValidationService,
    public integratedAdmissionService: IntegratedAdmissionService,
    public valorizationService: ValorizationService,
    public practiceService: PracticeService,
    public invoiceCashDeskService: InvoiceCashDeskService,
    public confirmDialogService: ConfirmDialogService,
    public budgetService: BudgetService,
    public dialog: MatDialog,
    public normativeService: NormativeService,
    public reservationService: ReservationService,
    public laboratoryAppointmentQuestionService: LaboratoryAppointmentQuestionService,
    public admissionGroupedProcedureRegionService: AdmissionGroupedProcedureRegionService,
  ) {
    this.centerId = this.authService.getCenterId();
    this.sectorId = this.authService.getSectorId();
  }

  abstract canExtractProcedures(): boolean;
  abstract getReturnUrl(): string;
  abstract cancel(): void;
  abstract finish(): Promise<void>;
  abstract getInitAdmission(): Promise<IntegratedAdmissionAppointment[]>;
  abstract getNewAdmission(): Promise<IntegratedAdmissionAppointment>;
  abstract showCopyAppointment(): boolean;
  abstract showAddAppointment(): boolean;
  abstract showPrint(): boolean;
  abstract getConfirmDialog(): Observable<boolean>;
  abstract canRefreshPracticeToParticular(): boolean;
  abstract linkAppointmentWait(): boolean;
  abstract showPreAdmissionPatientAppointment(): boolean;
  abstract validateProceduresForValidationLoaded(): boolean;

  isPreadmission(): boolean {
    return this.currentAppointment && this.currentAppointment.IsPre
      ? true
      : false;
  }

  validatePhoneRequired(): boolean {
    return this.authService.validatePhoneRequired() && !this.phoneValidated;
  }

  async groupProcedures() {
    if (
      !this.isAppointmentValid(this.currentAppointment, false, false, false) ||
      !this.proceduresGroupedRequired
    ) {
      return;
    }

    const items =
      this.currentAppointment.ValorizationEdit.ValorizationAmount
        .ValorizationItemsAmount;

    const procedureRegionIds = items.map(
      (i) => i.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId,
    );

    const groupedProcedures =
      await this.admissionGroupedProcedureRegionService.group(
        procedureRegionIds,
        this.currentAppointment.Plan.Entity.Id,
      );

    // Map PracticeRegionId -> items
    const itemsByRegionId = new Map<number, any[]>();

    for (const item of items) {
      const regionId =
        item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId;

      if (!itemsByRegionId.has(regionId)) {
        itemsByRegionId.set(regionId, []);
      }

      itemsByRegionId.get(regionId)!.push(item);
    }

    const groupedItems: any[][] = [];

    for (const group of groupedProcedures.filter((x) => x.IsGrouped)) {
      const groupItems: any[] = [];

      for (const regionId of group.ProcedureRegionIds) {
        const regionItems = itemsByRegionId.get(regionId);
        if (regionItems) {
          for (const item of regionItems) {
            groupItems.push(item);
          }
        }
      }

      if (groupItems.length > 0) {
        groupedItems.push(groupItems);
      }
    }

    const groupAppointment = new Map<IntegratedAdmissionAppointment, any[]>();

    let nexCurrentAppointment: IntegratedAdmissionAppointment;

    for (const group of groupedItems) {
      const appointment = JSON.parse(JSON.stringify(this.currentAppointment));
      nexCurrentAppointment = appointment;
      groupAppointment.set(appointment, group);
      await this.removeValorizationItemNoValidate(group);
    }

    for (const [appointment, group] of groupAppointment.entries()) {
      await this.addAppointment(appointment, group, false);
    }

    this.proceduresGroupedRequired = false;

    if (nexCurrentAppointment) {
      this.setCurrentAppointmentAndSendEvents(nexCurrentAppointment);
    }
  }

  async showPhoneFormatDialogIfIsRequired(): Promise<void> {
    if (this.validatePhoneRequired() && this.patientId) {
      const patient = await this.patientService.getPatientView(this.patientId);

      return new Promise<void>((resolve) => {
        const dialogRef = this.dialog.open(
          PatientPhoneFormatControlDialogComponent,
          {
            width: "800px",
            disableClose: true,
            data: {
              phone: patient.CellPhone,
              mail: patient.Email,
            },
          },
        );

        dialogRef.afterClosed().subscribe(async (result) => {
          if (result) {
            try {
              this.phoneValidated = true;

              await this.patientService.updatePatientCellPhone(
                this.patientId,
                result.phone,
              );

              await this.patientService.updatePatientMail(
                this.patientId,
                result.mail,
              );

              this._dataPatientCellPhoneSubject.next(result.phone);
            } catch (err) {
              this.notificationToastService.error(err.error, "Admisión");
            }
          }
          resolve();
        });
      });
    }

    return Promise.resolve();
  }

  async mergeBudget(budget: BudgetFilter) {
    try {
      const appointment = await this.budgetService.budgetingIntegratedAdmision(
        this.currentAppointment,
        budget.Id,
      );

      this.currentAppointment.BudgetId = budget.Id;
      this.currentAppointment.Plan = appointment.Plan;
      this.currentAppointment.ValorizationEdit = appointment.ValorizationEdit;
      this.currentAppointment.Scanns = appointment.Scanns;

      this._dataCurrentAppointmentSubject.next(appointment);
      this.senValorizationEvent("mergeBudget");

      if (appointment.Plan) {
        await this.setPlan(appointment.Plan);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  async mergeAppointmentWait(item: MedicalAppointmentWaitSearchItem) {
    try {
      const appointment =
        await this.integratedAdmissionService.medicalAppointmentWaitIntegratedAdmision(
          this.currentAppointment,
          item.Id,
        );

      this.currentAppointment.MedicalAppointmentWaitId = item.Id;
      this.currentAppointment.Plan = appointment.Plan;
      this.currentAppointment.ValorizationEdit = appointment.ValorizationEdit;
      this.currentAppointment.Scanns = appointment.Scanns;
      this.currentAppointment.Observation.AppointmentDate = item.Date;
      this.currentAppointment.Observation.Observation =
        appointment.Observation.Observation;
      this.currentAppointment.Observation.Diagnosis =
        appointment.Observation.Diagnosis;
      this.currentAppointment.Observation.IsInternation =
        appointment.Observation.IsInternation;

      this.currentAppointment.MedicalOrderEdit.MedicalOrderEditInfo.ReferringPhysician =
        appointment.MedicalOrderEdit.MedicalOrderEditInfo.ReferringPhysician;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.ReferringPhysicianId =
        appointment.MedicalOrderEdit.MedicalOrder.ReferringPhysicianId;

      this.currentAppointment.Validation.CredentialNumber =
        appointment.MedicalOrderEdit.MedicalOrder.CredentialNumber;

      this.currentAppointment.Validation.ReferringPhysician =
        appointment.MedicalOrderEdit.MedicalOrderEditInfo.ReferringPhysician;

      this._dataCurrentAppointmentSubject.next(appointment);
      this.senValorizationEvent("mergeAppointmentWait");

      if (appointment.Plan) {
        await this.setPlan(appointment.Plan);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  async init() {
    try {
      this.numberOfExtractedProcedures = 0;
      this.appointments = [];
      const initAppointments = await this.getInitAdmission();
      this.appointments.push(...initAppointments);
      this.currentAppointment = this.appointments[0];

      console.log(this.currentAppointment.Observation);

      this._dataAppointmentsSubject.next(this.appointments);
      this._dataCurrentAppointmentSubject.next(this.currentAppointment);

      this._dataCurrentValidatoinSubject.next(
        this.currentAppointment.Validation,
      );

      this._dataValidationProceduresSubject.next([]);

      if (this.currentAppointment.ValorizationEdit) {
        this.senValorizationEvent("init");
      }

      if (this.currentAppointment.Plan) {
        await this.setPlan(this.currentAppointment.Plan);
        await this.loadNomrative();
        if (this.currentAppointment.Plan.Entity) {
          this.proceduresGroupedRequired = this.currentAppointment.Plan.Entity.ProceduresGroupedRequired;
        }
      }

      this.invoiceCashDeskService
        .getCashDeskUserOpen(
          false,
          false,
          null,
          this.currentAppointment.ServiceId,
          null,
        )
        .then((cashDeskUserOpen) => {
          this.cashDeskUserOpen = cashDeskUserOpen;
        })
        .catch((err) => {
          console.log(err);
          this.notificationToastService.error(err.error, "Admisión");
        });
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    } finally {
      this._dataReadOnlySubject.next(this.readonly);
    }
  }

  setManualAgreementPriceIdNoValidated(ids: number[]): void {
    this.currentAppointment.ManualAgreementPriceIdNoValidated = ids;
    if (this.currentAppointment.Validation) {
      this.currentAppointment.Validation.ManualAgreementPriceIdNoValidated =
        ids;
    }
  }

  async addNewCopyAppointment() {
    if (this.isCurrentAppointmentValid()) {
      const noRegisteredGrouped = this.appointments.find(
        (a) => a.NoRegisteredGrouped === true,
      );

      const noValidatedItems = this.getValorizationItemNoValidate();
      const existNoValidated = noValidatedItems && noValidatedItems.length > 0;

      const appointment =
        existNoValidated && noRegisteredGrouped
          ? JSON.parse(JSON.stringify(noRegisteredGrouped))
          : JSON.parse(JSON.stringify(this.currentAppointment));

      await this.addAppointment(appointment, noValidatedItems);

      if (existNoValidated) {
        appointment.NoRegisteredGrouped = true;
      }

      this.setCurrentAppointmentAndSendEvents(appointment);
    }
  }

  async addAppointment(
    appointment: any,
    items: ValorizationItemAmount[],
    removeCurrentValorizationItemNoValidate: boolean = true,
  ) {
    try {
      appointment.registered = false;

      appointment.Appointment.Id = null;
      appointment.Validation.MedicalAppointmentValidation = null;
      appointment.procedures = [];
      appointment.Validation.AuthorizationNumber = null;
      appointment.Validation.AgreementPriceIdNoValidated = [];
      appointment.ManualAgreementPriceIdNoValidated = [];
      appointment.Validation.ManualAgreementPriceIdNoValidated = [];

      appointment.IsExtraordinary = true;

      if (items.length > 0) {
        if (removeCurrentValorizationItemNoValidate) {
          await this.removeCurrentValorizationItemNoValidate();
        }

        this.copyValorizationItems(
          appointment,
          JSON.parse(JSON.stringify(items)),
        );
      } else {
        appointment.ValorizationEdit = null;
      }

      const index = this.appointments.findIndex(
        (a) =>
          appointment.NoRegisteredGrouped === true &&
          a.NoRegisteredGrouped === true,
      );

      if (index !== -1) {
        this.appointments[index] = appointment;
      } else {
        this.appointments.push(appointment);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  async setCurrentAppointmentAndSendEvents(
    appointment: IntegratedAdmissionAppointment,
  ) {
    this.currentAppointment = appointment;

    this._dataAppointmentsSubject.next(this.appointments);

    this._dataCurrentAppointmentSubject.next(this.currentAppointment);

    if (
      this.currentAppointment.Validation &&
      !this.currentAppointment.validationLoaded
    ) {
      await this.setPlan(this.currentAppointment.Plan);
    }

    this._dataCurrentValidatoinSubject.next(this.currentAppointment.Validation);

    this._dataValidationProceduresSubject.next(
      this.currentAppointment.Validation.MedicalAppointmentValidation
        ? this.currentAppointment.Validation.MedicalAppointmentValidation
            .Procedures
        : this.currentAppointment.procedures,
    );
    this._dataCurrentProceduresSubject.next([]);
    this.senValorizationEvent("setCurrentAppointmentAndSendEvents");
  }

  private cloneValorization(valorization: ValorizationEdit): ValorizationEdit {
    return JSON.parse(JSON.stringify(valorization));
  }

  async addNewAppointment() {
    try {
      if (this.isCurrentAppointmentValid()) {
        const appointment = await this.getNewAdmission();
        appointment.PatientId = this.patientId;
        appointment.Plan = new IntegratedAdmissionAppointmentPlan();
        appointment.MedicalOrderEdit.MedicalOrder.PatientId = this.patientId;
        this.appointments.push(appointment);
        this.setCurrentAppointmentAndSendEvents(appointment);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  async setPatient(patientId: number) {
    try {
      this.patientId = patientId;
      this.phoneValidated = false;

      const budgets = await this.budgetService.getByPatientId(patientId);

      if (budgets.length > 0) {
        this.setBudget();
      } else if (this.linkAppointmentWait()) {
        await this.setAppointmentsWaitIfExistis(patientId);
      }

      this.appointments.forEach((a) => (a.PatientId = patientId));
      this.appointments.forEach(
        (a) => (a.MedicalOrderEdit.MedicalOrder.PatientId = patientId),
      );
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  private async setAppointmentsWaitIfExistis(patientId: number) {
    const now = new Date();
    const from = new Date(now.setDate(now.getDate() - 45));

    const appointmentsWait =
      await this.reservationService.getMedicalAppointmentWaitSearchItems(
        from,
        new Date(),
        null,
        EstadoTurnoEspera.EnEspera,
        patientId,
      );

    if (appointmentsWait.length > 0) {
      const dialogRef = this.dialog.open(AppointmentWaitDialogComponent, {
        width: "80%",
        data: appointmentsWait,
      });

      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          await this.mergeAppointmentWait(result);
        }
      });
    }
  }

  private async setBudget() {
    const dialogRef = this.dialog.open(BudgetListDialogComponent, {
      width: "80%",
      data: this.patientId,
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.mergeBudget(result);
      }
    });
  }

  canExtrarProceduresByIntents(): boolean {
    return true;
  }

  setExtractedProcedures(): void {
    this.numberOfExtractedProcedures++;
  }

  async reloadProcedures() {
    await this.loadProcedures(this.currentAppointment.Plan);
  }

  async setProcedureIdSelected(id: number, fromIA: boolean = false) {


    const procedure = this.procedures.find((p) => p.PracticeRegionId === id);

 console.log(procedure);

    if (procedure) {
      await this.setProcedureSelected(procedure, fromIA);
    }
  }

  async setProcedureSelected(
    procedure: PracticeRegionSearchReservation,
    fromIA: boolean = false,
  ) {
    if (!this.currentAppointment.Plan.Plan) {
      this.notificationToastService.error(
        "Debe seleccionar un plan para el procedimiento",
        "Admisión",
      );
      return;
    }

    if (!this.currentAppointment.Plan.AliquotTax) {
      this.notificationToastService.error(
        "Debe seleccionar una alícuota",
        "Admisión",
      );
      return false;
    }

    if (
      this.currentAppointment.Plan.Plan &&
      this.currentAppointment.Plan.AliquotTax
    ) {
      if (this.currentSelectedProducts().indexOf(procedure.Id) === -1) {
        await this.calculateValorization(procedure, fromIA);
      }
    }
  }

  async setCurrentAppointment(appointment: IntegratedAdmissionAppointment) {
    if (this.isCurrentAppointmentValid()) {
      await this.setCurrentAppointmentNoValidation(appointment);
    }
  }

  getAllCurrnetProcedureIds(): number[] {
    return this.currentAppointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
      (v) =>
        v.ValorizationItem.MedicalAppointmentProcedure.Quantity > 0 &&
        this.getAgreementPriceIdNoValidated().indexOf(
          v.ValorizationItem.AgreementPriceId,
        ) === -1,
    ).map((v) =>
      v.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionAditionalId
        ? v.ValorizationItem.MedicalAppointmentProcedure
            .PracticeRegionAditionalId
        : v.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId,
    );
  }

  setObservation(observation: IntegratedAdmissionAppointmentObservation): void {
    this.currentAppointment.Observation = observation;

    if (observation.DiagnosisCIE) {
      this.currentAppointment.MedicalOrderEdit.DiagnosisId =
        observation.DiagnosisCIE.Id;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.DiagnosisId =
        observation.DiagnosisCIE.Id;
    }
    this.currentAppointment.MedicalOrderEdit.OrderObservation =
      observation.Observation;
    this.currentAppointment.MedicalOrderEdit.PatientObservation =
      observation.ObservationPatient;
  }

  async setValorization(valorization: ValorizationEdit) {
    this.currentAppointment.ValorizationEdit =
      this.cloneValorization(valorization);
    await this.loadNomrative();
  }

  setDocuments(documents: DocumentationItem[]): void {
    this.currentAppointment.ValorizationEdit.DocumentationItems = [
      ...documents,
    ];
  }

  loadProceduresForValidation(
    procedures: MedicalAppointmentValidationProcedure[],
  ) {
    this.currentAppointment.proceduresForValidationLoaded = true;
    this.currentAppointment.procedures = procedures;
    this._dataValidationProceduresSubject.next(procedures);
  }

  setValidation(validation: IntegratedAdmissionAppointmentValidation): void {
    this.currentAppointment.Validation = validation;

    if (validation) {
      this.currentAppointment.MedicalOrderEdit.MedicalOrder.CredentialNumber =
        validation.CredentialNumber;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.SecurityCodeCredentialNumber =
        validation.Code;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.NroPreAuthorization =
        validation.PreAuth;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.PrescriptionDate =
        validation.PrescriptionDate;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.ReferringPhysicianId =
        validation.ReferringPhysician ? validation.ReferringPhysician.Id : null;

      this.currentAppointment.MedicalOrderEdit.MedicalOrder.ReferringPhysicianInstituteId =
        validation.ReferringPhysicianInstitute
          ? validation.ReferringPhysicianInstitute.Id
          : null;
    }
  }

  setScanns(scanns: MedicalOrderScann[]): void {
    this.currentAppointment.Scanns = [...scanns];
  }

  remove(appointment: IntegratedAdmissionAppointment) {
    this.appointments = this.appointments.filter((a) => a !== appointment);
    this._dataAppointmentsSubject.next(this.appointments);
    if (this.appointments.length > 0) {
      this.setCurrentAppointmentNoValidation(this.appointments[0]);
    } else {
      this.addNewAppointment();
    }
  }

  async setPlan(plan: IntegratedAdmissionAppointmentPlan) {
    try {

      console.log(plan);

      if (!plan || !plan.Plan || !plan.Entity) {
        return;
      }

      if (
        plan &&
        plan.Plan &&
        plan.Entity &&
        (!this.currentAppointment ||
          !this.currentAppointment.Plan ||
          !this.currentAppointment.Plan.Plan ||
          this.currentAppointment.Plan.Plan.Id !== plan.Plan.Id)
      ) {
        this.proceduresGroupedRequired = plan.Entity.ProceduresGroupedRequired;
      }

      this.currentAppointment.Plan = plan;

      if (
        plan.Plan &&
        (plan.Plan.Id !==
          this.currentAppointment.MedicalOrderEdit.MedicalOrder.PlanId ||
          !this.currentAppointment.validationLoaded)
      ) {
        this.currentAppointment.MedicalOrderEdit.MedicalOrder.PlanId =
          plan.Plan.Id;
        await this.loadValidation(plan.Plan);
        this.currentAppointment.validationLoaded = true;
      }
      this._dataCurrentPlanSubject.next(plan);
      await this.loadProcedures(plan);
      if (
        plan.Plan &&
        plan.AliquotTax &&
        this.currentAppointment.ValorizationEdit &&
        this.currentAppointment.ValorizationEdit.ValorizationAmount
      ) {
        this.currentAppointment.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
          plan.AliquotTax;

        const currentPlanId =
          this.currentAppointment.ValorizationEdit.ValorizationAmount.Plan.Id;

        this.currentAppointment.ValorizationEdit.ValorizationAmount.Plan =
          plan.Plan;

        this.currentAppointment.ValorizationEdit.ValorizationAmount.Entity =
          plan.Entity;

        this.currentAppointment.ValorizationEdit.ValorizationAmount.Valorization.PlanId =
          plan.Plan.Id;

        const valorizationEdit =
          await this.valorizationService.recalculateValorizationIntegratedProcedure(
            this.currentAppointment.ValorizationEdit,
            this.currentAppointment.Observation.AppointmentDate
              ? new Date()
              : this.currentAppointment.Observation.AppointmentDate,
            this.currentAppointment.Appointment.EquipmentId,
            currentPlanId,
          );

        await this.setValorization(valorizationEdit);
        this.senValorizationEvent("setPlan");
      }

      this._dataCurrentPlanSubject.next(plan);
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  async loadValidation(plan: Plan) {
    const conf =
      await this.integratedAdmissionService.getIntegratedAdmissionOrderConf(
        plan.Id,
        this.currentAppointment.IsPre,
      );

    if (!this.currentAppointment.Validation) {
      this.currentAppointment.Validation =
        new IntegratedAdmissionAppointmentValidation();
    }

    if (this.currentAppointment.validationLoaded) {
      this.currentAppointment.Validation.MedicalAppointmentValidation = null;
    }

    this.currentAppointment.Validation.InvalidOnError = conf.InvalidOnError;
    this.currentAppointment.Validation.InvalidOnRegister =
      conf.InvalidOnRegister;
    this.currentAppointment.Validation.InvalidOnVerification =
      conf.InvalidOnVerification;
    this.currentAppointment.Validation.IsRegister = conf.IsRegister;
    this.currentAppointment.Validation.IsVerification = conf.IsVerification;
    this.currentAppointment.Validation.Mask = conf.Mask;

    this.currentAppointment.Validation.RequiredCIEDiagnosis =
      conf.RequiredCIEDiagnosis;

    this.currentAppointment.Validation.DiagnosisCodeMode =
      conf.DiagnosisCodeMode;

    this.currentAppointment.Validation.RequiredPreAuth = conf.RequiredPreAuth;
    this.currentAppointment.Validation.RequiredCode = conf.RequiredSecurityCode;
    this.currentAppointment.Validation.RequiredCredentialNumber =
      conf.RequiredCredential;
    this.currentAppointment.Validation.RequiredValidation =
      conf.RequiredValidation;

    this._dataCurrentValidatoinSubject.next(this.currentAppointment.Validation);
    this._dataValidationProceduresSubject.next(
      this.currentAppointment.Validation.MedicalAppointmentValidation
        ? this.currentAppointment.Validation.MedicalAppointmentValidation
            .Procedures
        : this.currentAppointment.procedures,
    );
  }

  async refreshValorizationItem(
    item: ValorizationItemAmount,
    toParticular: boolean,
  ) {
    const valorizationEdit =
      await this.valorizationService.refreshItemInValorizationEditIntegratedProcedure(
        this.currentAppointment.ValorizationEdit,
        item,
        this.currentAppointment.Observation.AppointmentDate,
        this.currentAppointment.Appointment.EquipmentId,
        toParticular,
      );

    await this.setValorization(valorizationEdit);
    this.senValorizationEvent("refreshValorizationItem");
  }

  senValorizationEvent(source: string = "UNKNOWN") {
    console.groupCollapsed(
      `%c[VALORIZATION EVENT] from=${source}`,
      "color: #ff9800; font-weight: bold",
    );

    console.trace("CALL STACK");

    console.groupEnd();

    this._dataCurrentValorizationSubject.next(
      this.cloneValorization(this.currentAppointment.ValorizationEdit),
    );
  }

  async reverseRemoveValorizationItem(item: ValorizationItemAmount) {
    item.ValorizationItem.MedicalAppointmentProcedure.Quantity = 1;

    if (
      !item.ValorizationItem.MedicalAppointmentProcedure
        .PracticeRegionAditionalId &&
      !item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieId
    ) {
      const items =
        this.currentAppointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
          (x) =>
            x.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId ===
            item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId,
        );

      for (const i of items) {
        i.ValorizationItem.MedicalAppointmentProcedure.Quantity = 1;
      }
    }

    await this.revalorizate();
  }

  async removeValorizationItem(item: ValorizationItemAmount) {
    item.ValorizationItem.MedicalAppointmentProcedure.Quantity = 0;

    if (
      !item.ValorizationItem.MedicalAppointmentProcedure
        .PracticeRegionAditionalId &&
      !item.ValorizationItem.MedicalAppointmentProcedure.SetSupplieId
    ) {
      const items =
        this.currentAppointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
          (x) =>
            x.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId ===
            item.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId,
        );

      for (const i of items) {
        i.ValorizationItem.MedicalAppointmentProcedure.Quantity = 0;
      }
    }

    await this.revalorizate();
  }

  async revalorizate(affectParticular: boolean = false) {
    try {
      if (
        this.currentAppointment.ValorizationEdit &&
        this.currentAppointment.ValorizationEdit.ValorizationAmount &&
        this.currentAppointment.ValorizationEdit.ValorizationAmount
          .ValorizationItemsAmount &&
        this.getAllCurrnetProcedureIds().length > 0
      ) {
        this._dataLoadingValorizationSubject.next(true);

        const valorizationEdit =
          await this.valorizationService.recalculateValorizationIntegratedProcedure(
            this.currentAppointment.ValorizationEdit,
            this.currentAppointment.Observation.AppointmentDate,
            this.currentAppointment.Appointment.EquipmentId,
            null,
            affectParticular,
          );
        await this.setValorization(valorizationEdit);
        this.senValorizationEvent("revalorizate");
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    } finally {
      this._dataLoadingValorizationSubject.next(false);
    }
  }

  async getValidationProcedures(): Promise<
    MedicalAppointmentValidationProcedure[]
  > {
    return await this.onLineValidationService.getValidationProcedures(
      this.currentAppointment.Appointment,
      this.currentAppointment.MedicalOrderEdit.MedicalOrder,
      this.currentAppointment.ValorizationEdit,
    );
  }

  async executeValidation(): Promise<{ MedicalAppointmentValidation; Result }> {
    try {
      this.currentAppointment.Validation.HasError = false;

      if (
        this.currentAppointment.Validation.RequiredCIEDiagnosis &&
        !this.currentAppointment.MedicalOrderEdit.MedicalOrder.DiagnosisId
      ) {
        this.notificationToastService.error(
          "Debe ingresar el diagnósitco CIE",
          "Admisión",
        );
        return;
      }

      if (
        this.currentAppointment.Validation.RequiredCredentialNumber &&
        !this.currentAppointment.Validation.CredentialNumber
      ) {
        this.notificationToastService.error(
          "Debe ingresar el número de credencial",
          "Admisión",
        );
        return;
      }

      if (
        this.currentAppointment.Validation.RequiredCode &&
        !this.currentAppointment.Validation.Code
      ) {
        this.notificationToastService.error(
          "Debe ingresar el código de seguridad",
          "Admisión",
        );
        return;
      }

      const val = await this.onLineValidationService.executeOnLineOperation(
        this.currentAppointment.Appointment,
        this.currentAppointment.MedicalOrderEdit.MedicalOrder,
        this.currentAppointment.ValorizationEdit,
        this.currentAppointment.Validation.MedicalAppointmentValidation,
      );

      if (!val.NoValidate || val.PartialValidate) {
        this.currentAppointment.Validation.MedicalAppointmentValidation = val;

        this.currentAppointment.Validation.AuthorizationNumber = val.NeedCancel
          ? val.AuthorizationNumber
            ? val.AuthorizationNumber
            : val.ReferenceNumber
          : null;
      }

      if (this.currentAppointment.Validation.MedicalAppointmentValidation) {
        this.currentAppointment.Validation.MedicalAppointmentValidation.AgreementPriceIdNoValidated =
          val.AgreementPriceIdNoValidated;
      }

      this.currentAppointment.Validation.AgreementPriceIdNoValidated =
        val.AgreementPriceIdNoValidated;

      if (
        this.currentAppointment.Validation.MedicalAppointmentValidation &&
        !this.currentAppointment.Validation.MedicalAppointmentValidation
          .NoValidate &&
        this.currentAppointment.Validation.MedicalAppointmentValidation
          .AliquotTax &&
        this.currentAppointment.ValorizationEdit.ValorizationAmount.Valorization
          .AliquotTax.Id !==
          this.currentAppointment.Validation.MedicalAppointmentValidation
            .AliquotTax.Id
      ) {
        this.currentAppointment.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
          this.currentAppointment.Validation.MedicalAppointmentValidation.AliquotTax;
        this.currentAppointment.ValorizationEdit.ValorizationAmount =
          this.valorizationService.recalculatePatientTaxAmount(
            this.currentAppointment.ValorizationEdit.ValorizationAmount,
          );
      }

      this.senValorizationEvent("executeValidation");

      const message = val.DisplayMessage;

      if (val.NoValidate && !val.PartialValidate) {
        this._dataValidationProceduresSubject.next(val.Procedures);

        this.notificationToastService.error(message, "Validación on line");
      } else {
        this.currentAppointment.registered =
          this.currentAppointment.Validation.IsRegister && val.NeedCancel;
        this.notificationToastService.success(message, "Validación on line");
      }

      return {
        MedicalAppointmentValidation:
          this.currentAppointment.Validation.MedicalAppointmentValidation,
        Result: val,
      };
    } catch (err) {
      console.log(err);
      this.currentAppointment.Validation.HasError = true;
      this.notificationToastService.error(err.error, "Admisión", true);
    } finally {
      this._dataCurrentValidatoinSubject.next(
        this.currentAppointment.Validation,
      );
    }
  }

  private getAllProcedureRegionIds(
    appointment: IntegratedAdmissionAppointment,
  ): number[] {
    if (
      appointment.ValorizationEdit &&
      appointment.ValorizationEdit.ValorizationAmount &&
      appointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount
    ) {
      return appointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.map(
        (item) =>
          item.ValorizationItem.MedicalAppointmentProcedure
            .PracticeRegionAditionalId
            ? item.ValorizationItem.MedicalAppointmentProcedure
                .PracticeRegionAditionalId
            : item.ValorizationItem.MedicalAppointmentProcedure
                .PracticeRegionId,
      );
    }

    return [];
  }

  async showLaboratoryQuestios(atentionCode: string | null = null) {
    try {
      return new Promise<void>(async (resolve) => {
        const allProceduresIds: number[] = [];
        for (const appointment of this.appointments) {
          const procedureRegionIds = this.getAllProcedureRegionIds(appointment);

          if (procedureRegionIds.length > 0) {
            allProceduresIds.push(...procedureRegionIds);
          }
        }

        const questions =
          await this.laboratoryAppointmentQuestionService.getByProcedureRegion(
            allProceduresIds,
            atentionCode,
          );

        if (questions.length === 0) {
          resolve();
        } else {
          const dialogRef = this.dialog.open(
            LaboratoryAppointmentQuestionDialogComponent,
            {
              width: "1000px",
              maxHeight: "90vh",
              disableClose: true,
              data: questions,
            },
          );

          dialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
              try {
              } catch (err) {
                this.notificationToastService.error(err.error, "Admisión");
              }
            }
            resolve();
          });
        }
      });
    } catch (err) {
      console.log(err);
      alert(JSON.stringify(err));
    }
  }

  async create() {
    try {
      if (this.isCurrentAppointmentValid() && this.appointments.length > 0) {
        const response = await this.integratedAdmissionService.create(
          this.appointments,
        );

        let pendingPayment = false;

        if (response.AppointmentIds.length > 0) {
          pendingPayment = response.PaymentRequired;
        }

        await this.showLaboratoryQuestios(response.AtentionCode);

        if (confirm) {
          if (pendingPayment && this.cashDeskUserOpen) {
            this.router.navigate([
              "integrated-invoice-admission",
              response.AtentionCode,
              this.getReturnUrl(),
            ]);
          } else {
            this.router.navigate([
              "integrated-admission-operation",
              response.AtentionCode,
              this.getReturnUrl(),
            ]);
          }
        }
      }
    } catch (err) {
      console.log(err);
      if (err && err.statusText === "Unknown Error") {
        this.notificationToastService.error(
          "Por favor, controle la cantidad y tamaño de archivos adjuntos",
          "Admisión",
        );
      } else {
        this.notificationToastService.error(err.error, "Admisión");
      }
    }
  }

  async save() {
    try {
      if (this.appointments.length > 0 && this.isAllAppointmentsValid()) {
        await this.integratedAdmissionService.save(this.appointments);
        await this.showLaboratoryQuestios(this.appointments[0].Atention);
        this.cancel();
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    }
  }

  isCurrentAppointmentValid(): boolean {
    return this.isAppointmentValid(this.currentAppointment, true, true, true);
  }

  isAllAppointmentsValid(): boolean {
    return this.appointments.every((a) =>
      this.isAppointmentValid(a, false, true, true),
    );
  }

  isAppointmentValid(
    appointment: IntegratedAdmissionAppointment,
    validScann: boolean,
    validateOnLineValidation: boolean = true,
    validateGrupedProcedures: boolean = false,
  ): boolean {
    if (this.readonly) {
      return true;
    }

    if (validateGrupedProcedures && this.proceduresGroupedRequired) {
      this.notificationToastService.error(
        "Debe desagrupar los procedimientos",
        "Admisión",
      );
      return false;
    }

    if (!appointment.PatientId || appointment.PatientId === 0) {
      this.notificationToastService.error(
        "Debe seleccionar un paciente",
        "Admisión",
      );
      return false;
    }

    if (!appointment.Plan.AliquotTax) {
      this.notificationToastService.error(
        "Debe seleccionar una alícuota",
        "Admisión",
      );
      return false;
    }

    if (
      !appointment.ValorizationEdit ||
      !appointment.ValorizationEdit.ValorizationAmount ||
      !appointment.ValorizationEdit.ValorizationAmount
        .ValorizationItemsAmount ||
      this.getAllCurrnetProcedureIds().length === 0
    ) {
      this.notificationToastService.error(
        "No ha ingresado prácticas",
        "Admisión",
      );
      return false;
    }

    if (
      appointment.Observation.AppointmentDateRequired &&
      !appointment.Observation.AppointmentDate
    ) {
      this.notificationToastService.error(
        "Debe la fecha de atención",
        "Admisión",
      );
      return false;
    }

    if (
      appointment.Validation.RequiredCIEDiagnosis &&
      !appointment.MedicalOrderEdit.MedicalOrder.DiagnosisId
    ) {
      this.notificationToastService.error(
        "Debe ingresar el diagnósitco CIE",
        "Admisión",
      );
      return false;
    }

    if (validateOnLineValidation && appointment.Validation.RequiredValidation) {
      if (
        !appointment.Validation.MedicalAppointmentValidation ||
        !appointment.Validation.AuthorizationNumber
      ) {
        this.notificationToastService.error(
          "Debe realizar la validación on line",
          "Admisión",
        );
        return false;
      }

      if (appointment.Validation.RequiredCode && !appointment.Validation.Code) {
        this.notificationToastService.error(
          "Debe realizar el código de seguridad",
          "Admisión",
        );
        return false;
      }

      if (
        appointment.Validation.HasError &&
        appointment.Validation.InvalidOnError
      ) {
        this.notificationToastService.error(
          "Debe realizar la validación on line sin errores",
          "Admisión",
        );
        return false;
      }

      if (
        appointment.Validation.MedicalAppointmentValidation &&
        appointment.Validation.IsRegister &&
        !appointment.Validation.MedicalAppointmentValidation.NeedCancel &&
        appointment.Validation.InvalidOnRegister
      ) {
        this.notificationToastService.error(
          "No puede continuar si no se realizó la registración",
          "Admisión",
        );
        return false;
      }

      if (
        appointment.Validation.MedicalAppointmentValidation &&
        appointment.Validation.IsVerification &&
        appointment.Validation.MedicalAppointmentValidation.NoValidate ===
          true &&
        appointment.Validation.InvalidOnVerification
      ) {
        this.notificationToastService.error(
          "No puede continuar si no se realizó la verificación",
          "Admisión",
        );
        return false;
      }
    } else if (
      this.validateProceduresForValidationLoaded() &&
      validateOnLineValidation &&
      appointment.Plan.Entity.ShowValidation &&
      !appointment.proceduresForValidationLoaded
    ) {
      this.notificationToastService.error(
        "No se han aplicado las reglas",
        "Admisión",
      );
      return false;
    }

    if (
      !appointment.ValorizationEdit ||
      !appointment.ValorizationEdit.ValorizationAmount ||
      appointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount
        .length === 0
    ) {
      this.notificationToastService.error(
        "Debe seleccionar al menos un procedimiento",
        "Admisión",
      );
      return false;
    }

    if (!appointment.Validation.ReferringPhysician) {
      this.notificationToastService.error(
        "Debe seleccionar un médico derivante",
        "Admisión",
      );
      return false;
    }

    if (!appointment.Validation.PrescriptionDate) {
      this.notificationToastService.error(
        "Debe ingresar la fecha de prescripción",
        "Admisión",
      );
      return false;
    }

    if (
      !appointment.Appointment &&
      appointment.Appointment.Id === 0 &&
      !appointment.Observation.AppointmentDate
    ) {
      this.notificationToastService.error(
        "Debe ingresar la fecha del turno",
        "Admisión",
      );
      return false;
    }

    if (
      !appointment.Observation.Observation ||
      appointment.Observation.Observation === "" ||
      !appointment.Observation.Diagnosis ||
      appointment.Observation.Diagnosis === ""
    ) {
      this.notificationToastService.error(
        "Debe ingresar una observación y un diagnóstico",
        "Admisión",
      );
      return false;
    }

    const entity = appointment.Plan.Entity;

    if (entity.AthorizationValidity || entity.MedicalOrderValidity) {
      const validityDay = entity.AthorizationValidity
        ? entity.AthorizationValidityDay
        : entity.MedicalOrderValidityDay;
      const isValidDate = isValidMedicalOrderDate(
        appointment.Validation.PrescriptionDate,
        validityDay,
      );

      if (!isValidDate) {
        this.notificationToastService.error(
          "La fecha de prescripción no es válida",
          "Admisión",
        );
        return false;
      }
    }

    if (
      validScann &&
      (!appointment.Scanns || appointment.Scanns.length === 0)
    ) {
      this.notificationToastService.error(
        "Debe adjuntar al menos una imagen escaneada",
        "Admisión",
      );
      return false;
    }

    return true;
  }

  async removeCurrentValorizationItemNoValidate() {
    const noValidatedItems = this.getValorizationItemNoValidate();
    await this.removeValorizationItemNoValidate(noValidatedItems);
  }

  existsValidateedAppointmet(): boolean {
    return this.appointments.some((a) => a.registered);
  }

  private async loadNomrative() {
    const agreementPriceIds = this.getAgreementPriceIds();

    if (
      agreementPriceIds &&
      agreementPriceIds.length > 0 &&
      this.currentAppointment.Plan &&
      this.currentAppointment.Plan.Plan
    ) {
      const normative = await this.normativeService.getByParameters(
        agreementPriceIds,
        this.currentAppointment.Plan.Plan.Id,
      );

      if (
        normative &&
        (normative.EntityNormative ||
          normative.PlanNormative ||
          normative.PracticeNormative)
      ) {
        this._dataNormativeSubject.next(normative);
      } else {
        this._dataNormativeSubject.next(null);
      }
    } else {
      this._dataNormativeSubject.next(null);
    }
  }

  async loadProceduresForCurrentAppointment() {
    await this.loadProcedures(this.currentAppointment.Plan);
  }

  private async loadProcedures(plan: IntegratedAdmissionAppointmentPlan) {
    if (plan.Plan) {
      this.procedures =
        await this.practiceService.getFilteredPracticeRegionSearchReservation(
          plan.Plan.Id,
          this.currentAppointment.Appointment.PhysicianId,
          this.currentAppointment.Appointment.EquipmentId,
          this.centerId,
        );

      const currentSelectedProducts = this.currentSelectedProducts();

      if (currentSelectedProducts.length > 0) {
        const serviceId = this.currentAppointment.ServiceId;
        this.procedures = this.procedures.filter(
          (p) => p.ServiceId === serviceId,
        );

        this.procedures = this.procedures.filter(
          (p) => currentSelectedProducts.indexOf(p.Id) === -1,
        );
      }
      this._dataCurrentProceduresSubject.next(this.procedures);
    } else {
      this._dataCurrentProceduresSubject.next([]);
    }
  }

  private currentSelectedProducts(): number[] {
    if (
      this.currentAppointment.ValorizationEdit &&
      this.currentAppointment.ValorizationEdit.ValorizationAmount &&
      this.currentAppointment.ValorizationEdit.ValorizationAmount
        .ValorizationItemsAmount.length > 0
    ) {
      return this.currentAppointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.map(
        (v) => v.ValorizationItem.MedicalAppointmentProcedure.PracticeRegionId,
      );
    } else {
      return [];
    }
  }

  private async calculateValorization(
    procedure: PracticeRegionSearchReservation,
    fromIA: boolean = false,
  ) {
    if (!this.currentAppointment.Observation.AppointmentDate) {
      this.notificationToastService.error(
        "Debe ingresar la fecha del turno",
        "Admisión",
      );

      return;
    }
    try {
      this._dataLoadingValorizationSubject.next(true);
      if (
        !this.currentAppointment.ValorizationEdit ||
        !this.currentAppointment.ValorizationEdit.ValorizationAmount
      ) {
        const valorization =
          await this.valorizationService.getNewAdmissionValorizationIntegratedProcedure(
            this.currentAppointment.Observation.AppointmentDate,
            this.currentAppointment.Plan.Plan.Id,
            this.currentAppointment.Appointment.EquipmentId,
            [procedure.Id],
            this.centerId,
          );

        valorization.ValorizationEdit.ValorizationAmount.Valorization.AliquotTax =
          this.currentAppointment.Plan.AliquotTax;

        if (fromIA) {
          const item =
            valorization.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.find(
              (v) =>
                v.ValorizationItem.MedicalAppointmentProcedure
                  .PracticeRegionId === procedure.Id,
            );
          if (item) {
            item.ValorizationItem.MedicalAppointmentProcedure.FromIA = true;
          }
        }

        await this.setValorization(valorization.ValorizationEdit);
      } else {
        this.currentAppointment.ValorizationEdit.PracticeRegionAdd = procedure;
        const valorizationEditByProcedure =
          await this.valorizationService.addItemValorizationIntegratedProcedure(
            this.currentAppointment.ValorizationEdit,
            this.currentAppointment.Observation.AppointmentDate,
            this.currentAppointment.Plan.Plan.Id,
            this.currentAppointment.Appointment.EquipmentId,
            procedure.Id,
            this.centerId,
            this.currentAppointment.Appointment.PhysicianId,
          );

        if (fromIA) {
          const item =
            valorizationEditByProcedure.ValorizationAmount.ValorizationItemsAmount.find(
              (v) =>
                v.ValorizationItem.MedicalAppointmentProcedure
                  .PracticeRegionId === procedure.Id,
            );
          if (item) {
            item.ValorizationItem.MedicalAppointmentProcedure.FromIA = true;
          }
        }

        await this.setValorization(valorizationEditByProcedure);
      }
    } catch (err) {
      console.log(err);
      this.notificationToastService.error(err.error, "Admisión");
    } finally {
      await this.revalorizate();
    }
  }

  private getAgreementPriceIdNoValidated(): number[] {
    const manualAgreementPriceIdNoValidated =
      this.currentAppointment.ManualAgreementPriceIdNoValidated &&
      this.currentAppointment.ManualAgreementPriceIdNoValidated.length > 0
        ? this.currentAppointment.ManualAgreementPriceIdNoValidated
        : this.currentAppointment.Validation &&
            this.currentAppointment.Validation.AgreementPriceIdNoValidated
          ? this.currentAppointment.Validation.AgreementPriceIdNoValidated
          : [];

    return manualAgreementPriceIdNoValidated.length > 0
      ? manualAgreementPriceIdNoValidated
      : this.currentAppointment.Validation &&
          this.currentAppointment.Validation.MedicalAppointmentValidation &&
          this.currentAppointment.Validation.MedicalAppointmentValidation
            .AgreementPriceIdNoValidated &&
          this.currentAppointment.Validation.MedicalAppointmentValidation
            .AgreementPriceIdNoValidated.length > 0
        ? this.currentAppointment.Validation.MedicalAppointmentValidation
            .AgreementPriceIdNoValidated
        : manualAgreementPriceIdNoValidated;
  }

  private getValorizationItemNoValidate(): ValorizationItemAmount[] {
    const response: ValorizationItemAmount[] = [];

    let agreementPriceIdNoValidated = this.getAgreementPriceIdNoValidated();

    if (agreementPriceIdNoValidated) {
      for (let i = 0; i < agreementPriceIdNoValidated.length; i++) {
        const id = agreementPriceIdNoValidated[i];
        const items =
          this.currentAppointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
            (v) =>
              v.ValorizationItem.AgreementPriceId === id &&
              v.ValorizationItem.MedicalAppointmentProcedure.Quantity > 0 &&
              response.indexOf(v) === -1,
          );
        if (items && items.length > 0) {
          for (let j = 0; j < items.length; j++) {
            const item = items[j];
            response.push(item);
          }
        }
      }
    }

    return response;
  }

  private async removeValorizationItemNoValidate(
    noValidatedItems: ValorizationItemAmount[],
  ) {
    for (let i = 0; i < noValidatedItems.length; i++) {
      const item = noValidatedItems[i];
      if (item.ValorizationItem.MedicalAppointmentProcedure.Quantity > 0) {
        await this.removeValorizationItem(item);
      }
    }
  }

  private async setCurrentAppointmentNoValidation(
    appointment: IntegratedAdmissionAppointment,
  ) {
    this.setCurrentAppointmentAndSendEvents(appointment);
    await this.loadProcedures(appointment.Plan);
  }

  private getAgreementPriceIds(): number[] {
    if (
      this.currentAppointment.ValorizationEdit &&
      this.currentAppointment.ValorizationEdit.ValorizationAmount &&
      this.currentAppointment.ValorizationEdit.ValorizationAmount
        .ValorizationItemsAmount
    )
      return this.currentAppointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.filter(
        (v) => v.ValorizationItem.MedicalAppointmentProcedure.Quantity > 0,
      ).map((v) => v.ValorizationItem.AgreementPriceId);
    else return [];
  }

  private copyValorizationItems(
    appointment: any,
    noValidatedItems: ValorizationItemAmount[],
  ) {
    appointment.ValorizationEdit.ValorizationAmount.Valorization.MedicalAppointmentId =
      null;
    appointment.ValorizationEdit.ValorizationAmount.Valorization.Id = null;
    appointment.ValorizationEdit.ValorizationAmount.Valorization.MedicalAppointmentId =
      null;

    if (!appointment.NoRegisteredGrouped) {
      appointment.ValorizationEdit.DocumentationItems = [];
      appointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount =
        [];
    }

    noValidatedItems.forEach((v: ValorizationItemAmount) => {
      const nItem = JSON.parse(JSON.stringify(v));
      nItem.ValorizationItem.ValorizationId = null;
      nItem.ValorizationItem.Id = null;
      nItem.ValorizationItem.MedicalAppointmentProcedure.Quantity = 1;
      nItem.ValorizationItem.MedicalAppointmentProcedure.Id = null;
      nItem.ValorizationItem.MedicalAppointmentProcedure.FromIA = false;
      nItem.ValorizationItem.MedicalAppointmentProcedure.MedicalAppointmentId =
        null;
      appointment.ValorizationEdit.ValorizationAmount.ValorizationItemsAmount.push(
        nItem,
      );
    });
  }
}
